import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiRequestError } from '../../lib/api';
import { errorText } from '../../lib/crm';
import { PageHeader } from '../../components/AppShell';
import { LogoField } from '../../components/LogoField';
import { Button, Card, ErrorState, Field, Input, Skeleton, Textarea, useToast } from '../../components/ui';

/** Lists are edited one item per line. */
const LIST_FIELDS: Array<[string, string, string]> = [
  ['highlights', 'Introduction highlights', 'Short ticked points at the top of section 1'],
  ['introduction', 'Introduction paragraphs', 'One paragraph per line'],
  ['whyAdisys', 'Why ADISYS', 'One bullet per line'],
  ['customers', 'Esteemed customers', 'One name per line'],
  ['partners', 'Certified partners', 'One name per line'],
  ['onsiteSupport', 'On-site support', 'One bullet per line'],
  ['customerScope', 'Customer scope', 'One bullet per line'],
];
const TEXT_FIELDS: Array<[string, string, string?]> = [
  ['tagline', 'Tagline'], ['phones', 'Phone numbers'], ['email', 'Sales email'], ['website', 'Website'],
  ['gstin', 'ADISYS GSTIN', 'Printed in the footer of every page'], ['pan', 'ADISYS PAN'], ['cin', 'CIN'],
  ['stateName', 'State'], ['stateCode', 'GST state code', 'e.g. 29 for Karnataka'],
  ['offerTitle', 'Offer heading', 'e.g. BUDGETARY OFFER or COMMERCIAL OFFER'],
];

export function ProposalSettingsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['settings', 'all'], queryFn: () => api.get<{ data: any[] }>('/settings') });
  const [form, setForm] = useState<any>(null);
  const [error, setError] = useState<ApiRequestError | null>(null);

  useEffect(() => {
    const row = q.data?.data.find((s) => s.scope === 'crm' && s.key === 'proposal');
    if (row && !form) setForm(row.value);
  }, [q.data, form]);

  const save = useMutation({
    mutationFn: () => api.put('/settings/crm/proposal', {
      ...form,
      // text areas hold lines; the API wants arrays
      ...Object.fromEntries(LIST_FIELDS.map(([k]) => [k, toList(form[k])])),
      terms: toTerms(form.termsText ?? form.terms),
    }),
    onSuccess: () => { toast.success('Proposal content saved'); setError(null); void qc.invalidateQueries({ queryKey: ['settings'] }); },
    onError: (e) => { if (e instanceof ApiRequestError) setError(e); toast.error('Could not save', errorText(e)); },
  });

  if (q.isLoading || (!form && !q.error)) return <div className="p-6"><Skeleton className="h-96 w-full" /></div>;
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (!form) return <ErrorState error={new Error('The proposal settings have not been created. Run the database migrations.')} />;

  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const text = (k: string) => (Array.isArray(form[k]) ? form[k].join('\n') : (form[k] ?? ''));
  const termsText = form.termsText ?? (form.terms ?? []).map((t: any) => `${t.label}: ${t.text}`).join('\n');

  return (
    <>
      <PageHeader title="Proposal content"
        description="The standard ADISYS text and company details printed on every customer proposal and quotation email."
        actions={<Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save changes</Button>} />
      <div className="space-y-5 p-4 sm:p-6">
        {error && !error.details && <p className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">{error.message}</p>}
        <Card className="space-y-4 p-4 sm:p-5">
          <h2 className="text-sm font-semibold">Company details and footer</h2>
          <Field label="ADISYS logo" hint="Shown top-left on every page. Without one, the company name is printed.">
            <LogoField value={form.logoData ?? null} onChange={(v) => set('logoData', v)} />
          </Field>
          <Field label="Address"><Textarea rows={2} value={form.address ?? ''} onChange={(e) => set('address', e.target.value)} /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            {TEXT_FIELDS.map(([k, label, hint]) => (
              <Field key={k} label={label} hint={hint} error={error?.fieldError(k)}>
                <Input value={form[k] ?? ''} onChange={(e) => set(k, e.target.value)} />
              </Field>))}
          </div>
        </Card>

        <Card className="space-y-4 p-4 sm:p-5">
          <h2 className="text-sm font-semibold">Standard sections</h2>
          {LIST_FIELDS.map(([k, label, hint]) => (
            <Field key={k} label={label} hint={hint} error={error?.fieldError(k)}>
              <Textarea rows={k === 'customers' ? 8 : 5} value={text(k)} onChange={(e) => set(k, e.target.value)} />
            </Field>))}
          <Field label="Important note (section 6)"><Input value={form.importantNote ?? ''} onChange={(e) => set('importantNote', e.target.value)} /></Field>
          <Field label="Business terms" hint="One per line as “Label: text”. Delivery, payment and validity come from each quotation." error={error?.fieldError('terms')}>
            <Textarea rows={7} value={termsText} onChange={(e) => set('termsText', e.target.value)} />
          </Field>
          <Field label="Note under the price table"><Textarea rows={2} value={form.budgetaryNote ?? ''} onChange={(e) => set('budgetaryNote', e.target.value)} /></Field>
          <Field label="Conclusion" hint="{customer} is replaced with the customer company name.">
            <Textarea rows={5} value={form.conclusion ?? ''} onChange={(e) => set('conclusion', e.target.value)} />
          </Field>
        </Card>
      </div>
    </>
  );
}

const toList = (v: unknown): string[] =>
  (Array.isArray(v) ? v : String(v ?? '').split('\n')).map((s) => String(s).trim()).filter(Boolean);

function toTerms(v: unknown): Array<{ label: string; text: string }> {
  if (Array.isArray(v)) return v as any;
  return String(v ?? '').split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
    const i = l.indexOf(':');
    return i > 0 ? { label: l.slice(0, i).trim(), text: l.slice(i + 1).trim() } : { label: l, text: l };
  });
}
